import { useStoreState } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '@ge/components/button';
import { MiniLoader } from '@ge/components/loader';
import { Select } from '@ge/components/select';
import { useUpdateDamage } from '@ge/feat-inspections/data-hooks/use-damages';
import { useDefectTree } from '@ge/feat-inspections/data-hooks/use-parameters';
import {
  Capability,
  PermissionScope,
  DamageDiscription,
  SortDirection,
} from '@ge/models/constants';
import { DateMetaField } from '@ge/shared/components/meta-fields';
import { TaskProvider } from '@ge/shared/components/tasks/task-provider';
import { StyledDateContainer } from '@ge/shared/components/tasks/task-template-shared';
import { EntityDetailsContext } from '@ge/shared/context/entity-details-context';
import { useAuth } from '@ge/shared/data-hooks';
import { typography } from '@ge/tokens';
import { sorter } from '@ge/util/metric-sorter';

const DamageDetailsContainer = styled.div`
  display: flex;
  font-size: 12px;
  margin-top: ${(props) => (props.component == 'Blade' ? '10px' : '25px')};
  margin-left: 21px;
  position: relative;
`;

const FieldsLabel = styled.label`
  display: block;
  color: ${(props) => props.theme.themeSelector.customView.inputBorderColor};
  margin-bottom: 4px;
`;

const FieldValue = styled.p``;

const DamageDetailWrapper = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 100px;
`;

const DamageColumn = styled.div`
  width: 50%;
  justify-content: space-between;
  display: flex;
  flex-direction: column;
  gap: 20px;
  &.flex_item_left {
    width: 30%;
  }
  &.flex_item_right {
    display: table;
    .col {
      display: table-cell;
      width: 125px;
      &.label {
        text-align: right;
        padding-right: 10px;
      }
    }
    .row {
      margin-bottom: 20px;
    }
  }
`;

export const ButtonWrapper = styled.div`
  margin-right: 23px;
  position: absolute;
  right: 0;
  top: 0;
  h4,
  button {
    font-weight: ${typography.bold};
    text-transform: uppercase;
  }
  button {
    align-items: center;
    background: none;
    border: 0;
    color: ${(props) => props.theme.entityDetails.tasks.details.columnButtonColor};
    display: flex;
    font-size: 11px;
    margin-bottom: 10px;
    padding: 0 0 0 15px;
    position: relative;
    svg {
    }
    &:last-of-type {
      svg {
        left: 2px;
      }
    }
  }
`;

const StyledSelect = styled(Select)`
  width: ${(props) => (props.medium ? '170px' : '130px')};
  height: 25px;
  .select__control {
    max-height: 25px;
    align-content: center;
    div:first-of-type {
      max-height: 25px;
      max-width: 130px;
      width: 130px;
    }
    .select__indicators {
      width: 25px;
    }
  }
  .select__menu {
    .select__menu-list {
      max-height: 175px;
      &::-webkit-scrollbar {
        width: 4px;
        height: 0px;
      }
      &::-webkit-scrollbar-track {
        background: ${(props) => props.theme.scrollbar.trackBackground};
      }
      &::-webkit-scrollbar-thumb {
        background: ${(props) => props.theme.scrollbar.thumbBackground};
        border-radius: 2.5px;
      }
    }
  }
`;

const StyledFormDateContainer = styled(StyledDateContainer)`
  margin-right: 0px;
  .date-picker {
    .task-dialog-date {
      position: absolute;
      bottom: 7px;
    }
    input {
      width: 120px;
    }
  }
`;

const ButtonSection = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  margin: 20px 50px;
  padding-bottom: 20px;
`;

const CancelButton = styled(Button)`
  width: 75px;
  height: 24px;
  margin-right: 4px;
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.malAdjustmentTable.malStatusModal.cancelBtnBorderColor};
  background: ${(props) => props.theme.malAdjustmentTable.malStatusModal.cancelBtnBgColor};
  color: ${(props) => props.theme.malAdjustmentTable.malStatusModal.cancelBtnColor};
  font-size: 13px;
  font-weight: 600;
  line-height: 15.6px;
  text-align: center;
`;

const ConfirmButton = styled(Button)`
  width: 83px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid
    ${(props) => props.theme.malAdjustmentTable.malStatusModal.confirmBtnBorderColor};
  background: linear-gradient(180deg, #006e71 1.04%, #005e60 97.9%);
  color: #FFFFF;
  font-size: 13px;
  font-weight: 600;
  line-height: 15.6px;
  text-align: center;
`;

const LoaderContainer = styled.div`
  position: relative;
  width: 100%;
`;
const Label = styled.label`
  display: block;
  font-size: 11px;
  letter-spacing: 0;
  line-height: 13px;
  color: ${({ theme }) => theme.createReport.sendReportModal.labelColor};
  margin-bottom: 2px;
`;
const ErrorLabel = styled(Label)`
  color: ${({ theme }) => theme.createReport.sendReportModal.errorLabelColor};
  font-size: 13px;
  font-weight: bold;
  margin-top: 15px;
  position: absolute;
  left: 0;
  bottom: 2rem;
  margin: 20px 50px 20px 0;
`;

const setInitialSelectValue = (val) => {
  return val ? { value: val, label: val } : undefined;
};

const DamageEditPanel = (props) => {
  const { damage, onClose, isDamageLoading, setIsDamageLoading } = props;
  const { isAuthorized } = useAuth();
  const { setIsDamageUpdated } = useContext(EntityDetailsContext);
  const isDamageAuthorized = isAuthorized({
    capabilities: [{ capability: Capability.REI_RESULTS, scopes: [PermissionScope.DELETE] }],
  });
  const { t } = useTranslation(['inspections.damage-details'], {
    useSuspense: false,
  });
  const allSites = useStoreState((state) => state.sites.sites);
  const { getAssetById } = useStoreState((store) => store.assets);
  const [errorMessage, setErrorMessage] = useState(null);
  const { mutate: updateDamages } = useUpdateDamage();
  const [partValue, setPartValue] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedPart, setSelectedPart] = useState('');
  const [selectedLocationOption, setselectedLocationOption] = useState([]);
  const [selectedPartOption, setselectedPartOption] = useState([]);
  const [selectedDamageOption, setselectedDamageOption] = useState([]);
  const { formState, handleSubmit, watch, ...methods } = useForm({
    mode: 'onChange',
    defaultValues: {
      status: setInitialSelectValue(damage.status),
      category: setInitialSelectValue(damage.category),
      component: setInitialSelectValue(damage.component),
      location: setInitialSelectValue(damage.location),
      dateIdentified: damage?.dateIdentified ? new Date(damage.dateIdentified) : null,
      followupDate: damage?.requiredDate ? new Date(damage.requiredDate) : null,
      rulStartDate: damage?.rulStartDate ? new Date(damage.rulStartDate) : null,
      cat3DispositionDate: damage?.cat3DispositionDate
        ? new Date(damage.cat3DispositionDate)
        : null,
      cat3aDispositionDate: damage?.cat3aDispositionDate
        ? new Date(damage.cat3aDispositionDate)
        : null,
      cat4DispositionDate: damage?.cat4DispositionDate
        ? new Date(damage.cat4DispositionDate)
        : null,
      cat5DispositionDate: damage?.cat5DispositionDate
        ? new Date(damage.cat5DispositionDate)
        : null,
      part: setInitialSelectValue(damage.part),
      followupAction: setInitialSelectValue(damage.action),
      damage: !DamageDiscription.includes(damage.damageType)
        ? setInitialSelectValue('Unknown')
        : setInitialSelectValue(damage.damageType),
    },
  });

  const { data: defectTreeData = [] } = useDefectTree({
    inspectionType: 'caseManual',
  });

  const asset = getAssetById(damage?.assetSiteId);
  const siteData = asset?.site?.id ? allSites[asset?.site?.id] : allSites[damage?.assetSiteId];
  const modelStarts2x3x =
    siteData &&
    siteData?.turbineTypes.some(
      (turbine) => turbine.model.startsWith('2.') || turbine.model.startsWith('3.'),
    );

  const getOptions = (field) => {
    let data = defectTreeData?.items?.find((x) => x.access === field);
    if ('family_desc' === field && data?.values)
      data.values = data.values.filter((e) => e !== 'Other');

    if (!data || !data.values) {
      return [];
    }

    const mappedValues = data.values.map((value) => ({
      value: value,
      label: t(value, value.replace(/[_-]/g, ' ')),
    }));
    const sortedValues = mappedValues.sort((a, b) => a.label.localeCompare(b.label));

    return sortedValues;
  };
  const onSubmit = useCallback(
    (values) => {
      const isCat3DispositionVisible = getFieldsToShow(damage?.category).includes('Category 3');
      const isCat3aDispositionVisible = getFieldsToShow(damage?.category).includes('Category 3a');
      const isCat4DispositionVisible = getFieldsToShow(damage?.category).includes('Category 4');
      const isCat5DispositionVisible = getFieldsToShow(damage?.category).includes('Category 5');
      let payload = {
        id: damage.damageID,
        status: values[`status`]?.value,
        category: values[`category`]?.value ? values[`category`]?.value : damage.category,
        component: values[`component`]?.value,
        location: values[`location`]?.value,
        part: values[`component`]?.value === 'Generator' ? '' : values[`part`]?.value,
        // If others is selected from UI then free flowing text, else value from dropdown
        damage: values[`damage`]?.value,
        followupAction: values[`followupAction`]?.value,
        followupDate: values[`followupDate`]
          ? new Date(values[`followupDate`]).getTime() / 1000
          : null,
        rulStartDate: values[`rulStartDate`]
          ? new Date(values[`rulStartDate`]).getTime() / 1000
          : damage?.rulStartDate
          ? new Date(damage.rulStartDate).getTime() / 1000
          : null,
        cat3DispositionDate: values[`cat3DispositionDate`]
          ? new Date(values[`cat3DispositionDate`]).getTime() / 1000
          : isCat3DispositionVisible && isDamageAuthorized
          ? null
          : damage.cat3DispositionDate
          ? new Date(damage.cat3DispositionDate).getTime() / 1000
          : null,
        cat3aDispositionDate: values[`cat3aDispositionDate`]
          ? new Date(values[`cat3aDispositionDate`]).getTime() / 1000
          : isCat3aDispositionVisible && isDamageAuthorized
          ? null
          : damage.cat3aDispositionDate
          ? new Date(damage.cat3aDispositionDate).getTime() / 1000
          : null,
        cat4DispositionDate: values[`cat4DispositionDate`]
          ? new Date(values[`cat4DispositionDate`]).getTime() / 1000
          : isCat4DispositionVisible && isDamageAuthorized
          ? null
          : damage.cat4DispositionDate
          ? new Date(damage.cat4DispositionDate).getTime() / 1000
          : null,
        cat5DispositionDate: values[`cat5DispositionDate`]
          ? new Date(values[`cat5DispositionDate`]).getTime() / 1000
          : isCat5DispositionVisible && isDamageAuthorized
          ? null
          : damage.cat5DispositionDate
          ? new Date(damage.cat5DispositionDate).getTime() / 1000
          : null,
      };
      setIsDamageLoading(true);
      updateDamages(
        {
          payload,
        },
        {
          onSuccess: () => {
            onClose(true);
            setIsDamageUpdated(true);
          },
          onError: (error) => {
            setErrorMessage(error);
            setIsDamageLoading(false);
            setIsDamageUpdated(false);
            console.error(error);
          },
        },
      );
    },
    [updateDamages, damage],
  );

  const getFieldsToShow = (category) => {
    switch (category) {
      case 'Category 3':
        return ['Category 3'];
      case 'Category 3a':
        return ['Category 3', 'Category 3a'];
      case 'Category 4':
        return ['Category 3', 'Category 3a', 'Category 4'];
      case 'Category 5':
        return ['Category 3', 'Category 3a', 'Category 4', 'Category 5'];
      default:
        return [];
    }
  };

  const selectedCategory = watch(`category`)?.value || damage?.category;
  const visibleCategoryFields = getFieldsToShow(damage?.category);
  const isDispositionDateMissing =
    isDamageAuthorized &&
    ((selectedCategory === 'Category 3' &&
      !watch(`cat3DispositionDate`) &&
      visibleCategoryFields.includes('Category 3')) ||
      (selectedCategory === 'Category 3a' &&
        !watch(`cat3aDispositionDate`) &&
        visibleCategoryFields.includes('Category 3a')) ||
      (selectedCategory === 'Category 4' &&
        !watch(`cat4DispositionDate`) &&
        visibleCategoryFields.includes('Category 4')) ||
      (selectedCategory === 'Category 5' &&
        !watch(`cat5DispositionDate`) &&
        visibleCategoryFields.includes('Category 5')));

  const fieldsToShow = damage?.category ? getFieldsToShow(damage?.category) : [];
  const selectedCompObj =
    defectTreeData &&
    defectTreeData?.DamageHierarchy?.find((item) => item.component === selectedComponent);

  const getComponent = useCallback(
    (value) => {
      if (value) {
        setSelectedComponent(value);
        if (selectedComponent !== value) {
          setselectedPartOption([]);
          setselectedDamageOption([]);
          methods.setValue(`location`, null);
          methods.setValue(`part`, null);
          methods.setValue(`damage`, null);
          setPartValue(null);
          setSelectedLocation('');
          setSelectedPart('');
        }
      }
    },
    [methods, setSelectedComponent, selectedComponent],
  );
  const getLocation = useCallback(
    (value) => {
      if (value) {
        setSelectedLocation(value);
        if (selectedLocation !== value) {
          setselectedPartOption([]);
          setselectedDamageOption([]);
          setPartValue(null);
          setSelectedPart('');
          methods.setValue(`part`, null);
          methods.setValue(`damage`, null);
        }
      }
    },
    [setSelectedLocation, selectedLocation, methods],
  );
  const getPart = useCallback(
    (value, selectedOption) => {
      if (value) {
        setSelectedPart(value);
        setPartValue(selectedOption);
        if (selectedPart !== value) {
          setselectedDamageOption([]);

          methods.setValue(`damage`, null);
        }
      }
    },
    [setSelectedPart, setPartValue, selectedPart, methods],
  );

  useEffect(() => {
    if (selectedComponent === 'Generator') {
      setPartValue(null);
    }
  }, [selectedComponent]);

  useEffect(() => {
    if (damage.part) {
      setPartValue(setInitialSelectValue(damage.part));
    }
  }, [damage.part]);

  useEffect(() => {
    if (damage) {
      setSelectedComponent(damage?.component);
      setSelectedLocation(damage?.location);
      setSelectedPart(damage?.part);
    }
  }, [damage]);

  useEffect(() => {
    if (selectedComponent && selectedCompObj) {
      const locationOptions = selectedCompObj?.locations?.map((loc) => loc.value) || [];
      if (locationOptions) {
        const locationdDropdownOptions = locationOptions.map((item) => ({
          value: item,
          label: item,
        }));
        setselectedLocationOption(
          locationdDropdownOptions?.sort(sorter('value', SortDirection.ASC)),
        );
      }
    }
  }, [selectedComponent, selectedCompObj]);

  useEffect(() => {
    if (selectedLocation && selectedCompObj) {
      const locations = selectedCompObj?.locations?.find((loc) => loc.value === selectedLocation);
      const locationParts = locations?.parts?.map((part) => part.value);
      if (locationParts) {
        const partDropdownOptions = locationParts.map((item) => ({
          value: item,
          label: item,
        }));
        setselectedPartOption(partDropdownOptions?.sort(sorter('value', SortDirection.ASC)));
      }
      if (selectedComponent === 'Generator') {
        const locationParts = selectedCompObj?.locations?.find(
          (loc) => loc.value === selectedLocation,
        );
        const locationDamage = locationParts?.parts.flatMap((part) =>
          part.damages.map((d) => d.value),
        );
        const locationDamageOptions = locationDamage?.map((item) => ({
          value: item,
          label: item,
        }));
        setselectedDamageOption(locationDamageOptions?.sort(sorter('value', SortDirection.ASC)));
      }
    }
  }, [selectedLocation, selectedCompObj, selectedComponent]);

  useEffect(() => {
    if (selectedPart && selectedCompObj) {
      const parts = selectedCompObj?.locations
        .flatMap((loc) => loc.parts)
        .find((p) => p.value === selectedPart);
      const damagesOptions = parts?.damages.map((d) => d.value) || [];
      if (damagesOptions) {
        const damageDropdownOptions = damagesOptions.map((item) => ({
          value: item,
          label: item,
        }));
        const filteredDamageOptions = modelStarts2x3x
          ? damageDropdownOptions
          : damageDropdownOptions.filter((option) => option.value !== 'Main Shaft Drift');
        setselectedDamageOption(filteredDamageOptions?.sort(sorter('value', SortDirection.ASC)));
      }
    }
  }, [selectedPart, selectedCompObj, modelStarts2x3x]);

  return (
    <DamageDetailsContainer component={damage.component}>
      <DamageDetailWrapper>
        {isDamageLoading ? (
          <LoaderContainer>
            <MiniLoader />
          </LoaderContainer>
        ) : (
          <>
            <TaskProvider>
              <FormProvider {...methods}>
                <DamageColumn className="flex_item_left">
                  <div>
                    <FieldsLabel>{t(`damage_labels.damageId`, 'Damage ID')}</FieldsLabel>
                    <p>{damage.damageID}</p>
                  </div>
                  <Controller
                    name={`status`}
                    rules={{ required: true }}
                    // defaultValue={null}
                    render={({ onChange, value }) => (
                      <StyledSelect
                        label={`${t('case_panel.damage.form.status', 'Status')}*`}
                        onChange={onChange}
                        value={value}
                        options={getOptions('status')}
                        name={`status`}
                        error={methods.errors?.[`status`]?.type}
                      />
                    )}
                  />
                  <Controller
                    name={`component`}
                    rules={{ required: true }}
                    render={({ onChange, value }) => (
                      <StyledSelect
                        label={`${t('case_panel.damage.form."component"', 'Component')}*`}
                        onChange={(selectedOption) => {
                          onChange(selectedOption);
                          getComponent(selectedOption?.value);
                        }}
                        value={value}
                        options={getOptions('component')}
                        name={`component`}
                        error={methods.errors?.[`component`]?.type}
                      />
                    )}
                  />
                  <StyledFormDateContainer>
                    <div className="label">
                      {`${t('case_panel.damage.form.dateIdentified', 'Date Identified')}`}
                    </div>
                    <FieldValue className="col">{damage.dateIdentified}</FieldValue>
                  </StyledFormDateContainer>
                  <StyledFormDateContainer>
                    <div className="label">
                      {`${t('case_panel.damage.form.follow_up_date', 'Required Date')}`}
                    </div>
                    <DateMetaField
                      metadata={{ required: false, readOnly: false, isInput: true }}
                      name={`followupDate`}
                      validators={[{ latestDate: 'latestDate' }]}
                    />
                  </StyledFormDateContainer>
                  {selectedCategory !== 'Category 2' &&
                    (isDamageAuthorized ? (
                      <StyledFormDateContainer>
                        <div className="label">
                          {`${t(
                            'case_panel.damage.form."cat3_first_indication_date_damage"',
                            'RUL Start Date',
                          )}*`}
                        </div>
                        <DateMetaField
                          metadata={{ required: false, readOnly: false, isInput: true }}
                          name={`rulStartDate`}
                          max={new Date()}
                          validators={[{ latestDate: 'latestDate' }]}
                        />
                      </StyledFormDateContainer>
                    ) : (
                      <StyledFormDateContainer>
                        <div className="label">
                          {`${t(
                            'case_panel.damage.form.cat3_first_indication_date_damage',
                            'RUL Start Date',
                          )}`}
                        </div>
                        <FieldValue className="col">{damage.rulStartDate}</FieldValue>
                      </StyledFormDateContainer>
                    ))}
                  {fieldsToShow.includes('Category 3') && (
                    <>
                      {isDamageAuthorized ? (
                        <StyledFormDateContainer>
                          <div className="label">
                            {`${t(
                              'case_panel.damage.form."cat3_cat3_logged_date"',
                              'Category 3 Disposition Date',
                            )}`}
                          </div>
                          <DateMetaField
                            metadata={{ required: false, readOnly: false, isInput: true }}
                            name={`cat3DispositionDate`}
                            max={new Date()}
                            validators={[{ latestDate: 'latestDate' }]}
                          />
                        </StyledFormDateContainer>
                      ) : (
                        <StyledFormDateContainer>
                          <div className="label">
                            {`${t(
                              'case_panel.damage.form.cat3_logged_date',
                              'Category 3 Disposition Date',
                            )}`}
                          </div>
                          <FieldValue className="col">{damage.cat3DispositionDate}</FieldValue>
                        </StyledFormDateContainer>
                      )}
                    </>
                  )}
                  {fieldsToShow.includes('Category 3a') && (
                    <>
                      {isDamageAuthorized ? (
                        <StyledFormDateContainer>
                          <div className="label">
                            {`${t(
                              'case_panel.damage.form."cat3a_logged_date"',
                              'Category 3a Disposition Date',
                            )}`}
                          </div>
                          <DateMetaField
                            metadata={{ required: false, readOnly: false, isInput: true }}
                            name={`cat3aDispositionDate`}
                            max={new Date()}
                            validators={[{ latestDate: 'latestDate' }]}
                          />
                        </StyledFormDateContainer>
                      ) : (
                        <StyledFormDateContainer>
                          <div className="label">
                            {`${t(
                              'case_panel.damage.form.cat3a_logged_date',
                              'Category 3a Disposition Date',
                            )}`}
                          </div>
                          <FieldValue className="col">{damage.cat3aDispositionDate}</FieldValue>
                        </StyledFormDateContainer>
                      )}
                    </>
                  )}
                  {fieldsToShow.includes('Category 4') && (
                    <>
                      {isDamageAuthorized ? (
                        <StyledFormDateContainer>
                          <div className="label">
                            {`${t(
                              'case_panel.damage.form."cat4_logged_date"',
                              'Category 4 Disposition Date',
                            )}`}
                          </div>
                          <DateMetaField
                            metadata={{ required: false, readOnly: false, isInput: true }}
                            name={`cat4DispositionDate`}
                            max={new Date()}
                            validators={[{ latestDate: 'latestDate' }]}
                          />
                        </StyledFormDateContainer>
                      ) : (
                        <StyledFormDateContainer>
                          <div className="label">
                            {`${t(
                              'case_panel.damage.form.cat4_logged_date',
                              'Category 4 Disposition Date',
                            )}`}
                          </div>
                          <FieldValue className="col">{damage.cat4DispositionDate}</FieldValue>
                        </StyledFormDateContainer>
                      )}
                    </>
                  )}
                  {fieldsToShow.includes('Category 5') && (
                    <>
                      {isDamageAuthorized ? (
                        <StyledFormDateContainer>
                          <div className="label">
                            {`${t(
                              'case_panel.damage.form."cat5_logged_date"',
                              'Category 5 Disposition Date',
                            )}`}
                          </div>
                          <DateMetaField
                            metadata={{ required: false, readOnly: false, isInput: true }}
                            name={`cat5DispositionDate`}
                            max={new Date()}
                            validators={[{ latestDate: 'latestDate' }]}
                          />
                        </StyledFormDateContainer>
                      ) : (
                        <StyledFormDateContainer>
                          <div className="label">
                            {`${t(
                              'case_panel.damage.form.cat5_logged_date',
                              'Category 5 Disposition Date',
                            )}`}
                          </div>
                          <FieldValue className="col">{damage.cat5DispositionDate}</FieldValue>
                        </StyledFormDateContainer>
                      )}
                    </>
                  )}
                </DamageColumn>

                <DamageColumn className="flex_item_right">
                  <div className="row">
                    <FieldsLabel className="col label">{`${t(
                      'location',
                      'Location',
                    )}*`}</FieldsLabel>
                    <div className="col">
                      <Controller
                        name={`location`}
                        rules={{ required: true }}
                        render={({ onChange, value }) => (
                          <StyledSelect
                            onChange={(selectedOption) => {
                              onChange(selectedOption);
                              getLocation(selectedOption?.value);
                            }}
                            value={value}
                            options={selectedLocationOption}
                            name={`location`}
                            error={methods.errors?.[`location`]?.type}
                            medium
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <FieldsLabel className="col label">{`${t(
                      'case_panel.damage.form.part',
                      'Part',
                    )}${watch(`component`)?.value !== 'Generator' ? '*' : ''}`}</FieldsLabel>
                    <div className="col">
                      {watch(`component`)?.value !== 'Generator' && (
                        <Controller
                          name={`part`}
                          rules={{ required: true }}
                          render={({ onChange }) => (
                            <StyledSelect
                              onChange={(selectedOption) => {
                                onChange(selectedOption);
                                getPart(selectedOption?.value, selectedOption);
                              }}
                              value={partValue}
                              options={selectedPartOption}
                              name={`part`}
                              error={methods.errors?.[`part`]?.type}
                              medium
                            />
                          )}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <FieldsLabel className="col label">{`${t(
                      'damage_data.subFamily',
                      'Sub Part',
                    )}`}</FieldsLabel>
                    <div className="col">
                      <FieldValue className="col">{damage.subFamily}</FieldValue>
                    </div>
                  </div>
                  <div className="row">
                    <FieldsLabel className="col label">{`${t(
                      'damage_data.damage',
                      'Damage Type',
                    )}*`}</FieldsLabel>
                    <div className="col">
                      <Controller
                        name={`damage`}
                        rules={{ required: true }}
                        render={({ onChange, value }) => (
                          <StyledSelect
                            onChange={onChange}
                            value={value}
                            options={selectedDamageOption}
                            name={`damage`}
                            error={methods.errors?.[`damage`]?.type}
                            medium
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <FieldsLabel className="col label">
                      {`${t('case_panel.damage.form.category', 'Category')}*`}
                    </FieldsLabel>
                    <div className="col">
                      {isDamageAuthorized ? (
                        <Controller
                          name={`category`}
                          rules={{ required: true }}
                          render={({ onChange, value }) => (
                            <StyledSelect
                              onChange={onChange}
                              value={value}
                              options={getOptions('severity')}
                              name={`category`}
                              error={methods.errors?.[`category`]?.type}
                              medium
                            />
                          )}
                        />
                      ) : (
                        <FieldValue className="col">{damage.category}</FieldValue>
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <FieldsLabel className="col label">{`${t(
                      'damage_data.probableCause',
                      'Probable Cause',
                    )}`}</FieldsLabel>
                    <div className="col">
                      <FieldValue className="col">{damage.probableCause}</FieldValue>
                    </div>
                  </div>
                  <div className="row">
                    <FieldsLabel className="col label">{`${t(
                      'damage_data.action',
                      'Action',
                    )}*`}</FieldsLabel>
                    <div className="col">
                      <Controller
                        name={`followupAction`}
                        rules={{ required: true }}
                        render={({ onChange, value }) => (
                          <StyledSelect
                            onChange={onChange}
                            value={value}
                            options={getOptions('action')}
                            name={`followupAction`}
                            error={methods.errors?.[`followupAction`]?.type}
                            medium
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <FieldsLabel className="col label">{`${t(
                      'damage_data.followUp',
                      'Follow-up',
                    )}`}</FieldsLabel>
                    <div className="col">
                      <FieldValue className="col">{damage.followUp}</FieldValue>
                    </div>
                  </div>
                  <div className="row">
                    <FieldsLabel className="col label">{`${t(
                      'damage_data.spanZ',
                      'Span Z (m)',
                    )}`}</FieldsLabel>
                    <FieldValue className="col">{damage.spanZ}</FieldValue>
                  </div>
                  <div className="row">
                    <FieldsLabel className="col label">{`${t(
                      'damage_data.width',
                      'Size SW (mm)',
                    )}`}</FieldsLabel>
                    <FieldValue className="col">{damage.width}</FieldValue>
                  </div>
                  <div className="row">
                    <FieldsLabel className="col label">{`${t(
                      'damage_data.length',
                      'Size CW (mm)',
                    )}`}</FieldsLabel>
                    <FieldValue className="col">{damage.length}</FieldValue>
                  </div>
                  <div className="row">
                    <FieldsLabel className="col label">{`${t(
                      'damage_data.chord',
                      'Chord Y(%)',
                    )}`}</FieldsLabel>
                    <FieldValue className="col">{damage.chord}</FieldValue>
                  </div>
                  <div className="row">
                    <FieldsLabel className="col label">{`${t(
                      'damage_data.rul_P50',
                      'RUL P50',
                    )}`}</FieldsLabel>
                    <FieldValue className="col">{damage.rul50}</FieldValue>
                  </div>
                  <div className="row">
                    <FieldsLabel className="col label">{`${t(
                      'damage_data.rul_P90',
                      'RUL P90',
                    )}`}</FieldsLabel>
                    <FieldValue className="col">{damage.rul90}</FieldValue>
                  </div>
                </DamageColumn>
              </FormProvider>
            </TaskProvider>
          </>
        )}
      </DamageDetailWrapper>

      {errorMessage &&
        errorMessage?.response?.data?.description &&
        errorMessage?.response?.data?.code === 400 && (
          <ErrorLabel>{errorMessage?.response?.data?.description}</ErrorLabel>
        )}
      {errorMessage && errorMessage?.response?.data?.code !== 400 && (
        <ErrorLabel>
          {t('case_panel.damage.form.saveErr', 'An error occurred while saving changes.')}
        </ErrorLabel>
      )}
      <ButtonSection onClick={(e) => e.stopPropagation()}>
        <CancelButton onClick={() => onClose(false)}>
          {t('approve_reject_dialog.cancel', 'Cancel')}
        </CancelButton>
        <ConfirmButton
          disabled={
            !formState.isValid ||
            isDamageLoading ||
            (isDamageAuthorized && selectedCategory !== 'Category 2' && !watch(`rulStartDate`)) ||
            isDispositionDateMissing
          }
          onClick={handleSubmit(onSubmit)}
          primary
        >
          {t('case_panel.damage.save', 'Save')}
        </ConfirmButton>
      </ButtonSection>
    </DamageDetailsContainer>
  );
};

DamageEditPanel.propTypes = {
  damage: PropTypes.object,
  onClose: PropTypes.func,
  isDamageLoading: PropTypes.bool,
  setIsDamageLoading: PropTypes.func,
  inspectionType: PropTypes.any,
};

export default DamageEditPanel;
